import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'

/**
 * Auto-login page for self-signup flow.
 * Receives a JWT token via ?token= query param,
 * fetches the user profile, and logs them in.
 */
export function AutoLoginPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const token = searchParams.get('token')
    if (!token) {
      setError('No authentication token provided.')
      return
    }

    // Fetch the real user profile using the token
    fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Invalid token')
        return res.json()
      })
      .then((data) => {
        const user = data.data
        login(
          {
            ...user,
            role: user.role || 'ADMIN',
            status: user.status || 'active',
          },
          token
        )
        navigate('/dashboard', { replace: true })
      })
      .catch((err) => {
        console.error('Auto-login failed:', err)
        setError('Login failed. Please try logging in manually.')
      })
  }, [searchParams, navigate, login])

  if (error) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: 'system-ui, sans-serif', color: '#64748B',
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#1E293B', fontSize: 18, marginBottom: 8 }}>Login Failed</h2>
          <p>{error}</p>
          <p style={{ marginTop: 16 }}>
            <a href="/login" style={{ color: '#3B82F6' }}>Go to login</a>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', fontFamily: 'system-ui, sans-serif', color: '#64748B',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ margin: '0 auto 16px', width: 32, height: 32, border: '3px solid #E2E8F0', borderTopColor: '#3B82F6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        <p>Setting up your account...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  )
}
